import type { EvolutionRegimesProbabilities } from "@eu.mdotm.api/sphere";
import GraphLegend from "$root/components/GraphLegend";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { hmmRepository } from "$root/functional-areas/hmm/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts, useHighchartsDiscretizedZoom } from "$root/third-party-integrations/highcharts-with-modules";
import { type ContextContent, days, withContext } from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { RegionContext } from "$root/widgets-architecture/contexts/region";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { type ImperativeHandleRefProps, useRefProxy } from "@mdotm/shadcn/legacy/react-extensions";
import BigNumber from "bignumber.js";
import Color from "color";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import React, { type ForwardedRef, useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

type PercentageChartProps = {
	series: Array<{
		name: string;
		legendName?: string;
		color: string;
		yValues: number[];
	}>;
	xTimestamps: number[];
	chartRef?: ForwardedRef<HighchartsReactRefObject>;
	title?: string;
};

const PercentageChart: React.FC<PercentageChartProps> = ({ series, xTimestamps, chartRef, title }) => {
	const { t } = useTranslation();
	const internalChartRef = useRefProxy<HighchartsReactRefObject | null>(null, chartRef);
	const { formatNumber, formatDate } = useLocaleFormatters();
	BigNumber.config({ ROUNDING_MODE: BigNumber.ROUND_HALF_DOWN });

	const { afterSetExtremes } = useHighchartsDiscretizedZoom();

	const options = useMemo(() => {
		return {
			chart: {
				type: "area",
				style: {
					fontFamily: "Gotham,sans-serif",
				},
				panning: {
					enabled: false,
				},
				displayErrors: true,
			},
			credits: {
				enabled: false,
			},
			legend: { enabled: false },
			title: { text: undefined },
			subtitle: { text: undefined },
			navigator: {
				series: {
					lineColor: "transparent",
					color: "transparent",
				},
			},
			xAxis: [
				{
					type: "datetime",
					tickWidth: 0,
					minRange: days(1),
					endOnTick: false,
					startOnTick: false,
					min: xTimestamps[0],
					max: xTimestamps[xTimestamps.length - 1],
					crosshair: {
						width: 1,
						color: "black",
						dashStyle: "shortdash",
						zIndex: 2,
					},
					gridLineWidth: 0,
					events: { afterSetExtremes },
				},
			],
			yAxis: {
				gridLineWidth: 0,
				min: 0,
				softMax: 1, // allow chart show value evaluated more than 1
				endOnTick: false, //allow the chart to extend beyond the softMax value while still filling the available height.
				labels: {
					align: "center",
					formatter: ({ value }: { value: number }) => formatNumber(Math.floor(value * 100), 0) + "%",
				},
				title: {
					enabled: false,
				},
				crosshair: {
					width: 1,
					color: "black",
					dashStyle: "shortdash",
					zIndex: 2,
				},
			},
			boost: {
				debug: {
					timeRendering: true,
				},
				useGPUTranslations: true,
				seriesThreshold: 1,
			},
			tooltip: {
				shared: false,
				useHTML: true,
				distance: 72,
				backgroundColor: "#FFFFFF",
				borderColor: "#FFFFFF",
				borderRadius: 8,
				shadow: {
					color: "#4a4a4a",
				},
				style: {
					padding: "0",
					color: "#656d78",
					fontSize: "12px",
				},
				formatter(this: {
					points: {
						x: number;
						y: number;
						series: Highcharts.Series & { options: { marker: { fillColor: string } } };
					}[];
					x: number;
					y: number;
				}) {
					const content = `<div style="min-width: 12.5rem">
							<div style="text-align: center;
								border-radius: 0.25rem;
								background-color: #eeeef1;
								color: #9699b3;
								font-family: Gotham, sans-serif;
								font-weight: bold;
								line-height: 1.25rem;
								padding: 0.2rem 0;">
								<div>${t("EVOLUTION_OF_REGIME_PROBABILITIES.tooltip")}</div>
							</div>
							<div
								style="color: #9699b3;
								text-align: center;
								font-family: Gotham, sans-serif;
								font-weight: bold;
								padding: 0.4rem 0"
							>${formatDate(this.x)} - ${formatDate(this.x + days(7))}</div>
							<table style="width: 100%">
								<tbody>${(this.points || [])
									.map(
										(point) => `
										<tr>
											<td style="border-bottom: solid 1px transparent; padding: 5px 8px 0 0">
											<span style="display: flex; flex-direction: row; align-items: center">
												<span style="display: inline-block; width: 10px; height: 10px; margin-right: 6px; border-radius: 100px; background-color: ${
													point.series.options.marker.fillColor
												}">
											</span>
											${point.series.name}</span>
											</td>
											<td
												style="text-align: right; font-weight: 500; border-bottom: solid 1px transparent; padding: 5px 0 0 0; color: black"
											>
												${formatNumber(point.y * 100, 0)}%
											</td>
										</tr>
									`,
									)
									.reverse()
									.join("")}
								</tbody>
							</table>
						</div>`;
					return content;
				},
			},
			plotOptions: {
				area: {
					stacking: "normal",
					lineColor: "#ffffff",
					lineWidth: 2,
					marker: {
						lineWidth: 1,
						symbol: "dot",
					},
				},
			},
			series: [...series].reverse().map((singleSeries) => {
				const baseColor = singleSeries.color;
				const transparentColor = Color(singleSeries.color).alpha(0.35).toString();
				return {
					lineColor: baseColor,
					color: transparentColor,
					marker: {
						color: baseColor,
						fillColor: baseColor,
					},
					name: singleSeries.name,
					data: singleSeries.yValues.map((y, i) => ({
						y,
						x: xTimestamps[i],
					})),
					turboThreshold: xTimestamps.length,
				};
			}),
			exporting: {
				enabled: true,
				chartOptions: {
					title: {
						text: title ?? t("EVOLUTION_OF_REGIME_PROBABILITIES.title"),
					},
					subtitle: {
						text: `Data Exported in ${formatDate(new Date())}`,
					},
					chart: {
						margin: 40,
						marginTop: 80,
						marginLeft: 80,
						marginBottom: 80,
					},
					scrollbar: {
						enabled: false,
					},
				},
				buttons: {
					contextButton: {
						menuItems: ["downloadJPEG", "downloadPDF"],
					},
				},
				allowHTML: true,
				printMaxWidth: 1080,
				filename: title ?? t("EVOLUTION_OF_REGIME_PROBABILITIES.title"),
				sourceHeight: 400,
				sourceWidth: 1440,
			},
		};
	}, [xTimestamps, afterSetExtremes, series, title, t, formatNumber, formatDate]);

	return (
		<div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
			<div style={{ borderRadius: 4, borderColor: "#eeeef1", borderWidth: 2, borderStyle: "solid", flex: 1 }}>
				<HighchartsReact
					containerProps={{ style: { height: 300 } }}
					highcharts={Highcharts}
					constructorType="stockChart"
					options={options}
					ref={internalChartRef}
				/>
			</div>
			<div>
				<div style={{ flex: 0 }}>
					<GraphLegend>
						{series.map((singleSeries) => (
							<div key={singleSeries.color} className="legend-container return-analysis light more-space book">
								<ColoredRectangle variant="vertical" color={singleSeries.color} />
								{singleSeries.legendName ?? singleSeries.name}
							</div>
						))}
					</GraphLegend>
				</div>
			</div>
		</div>
	);
};

const EvolutionOfRegimeProbabilitiesBlock = (props: ContextContent<typeof RegionContext>) => {
	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: t("EVOLUTION_OF_REGIME_PROBABILITIES.title"),
		}),
		[t],
	);
	const region = props.region;
	const evolutionOfRegimeRaw = hmmRepository.evolutionOfRegime.useQuery(region);
	const evolutionOfRegime = useMappedQuery(evolutionOfRegimeProbabilitiesToSeries, [], evolutionOfRegimeRaw);

	return (
		<ReactQueryWrapperBase
			query={evolutionOfRegime}
			errorFallback={<IconWalls.ErrorData />}
			loadingFallback={<IconWalls.LoadingData />}
		>
			{(series) => <EvolutionOfRegimeProbabilitiesInner series={series} />}
		</ReactQueryWrapperBase>
	);
};

export function evolutionOfRegimeProbabilitiesToSeries(regimeEvolution: EvolutionRegimesProbabilities): {
	DateSeries: number[];
	GrowSeries: number[];
	LateralSeries: number[];
	HighSeries: number[];
} {
	const DateSeries = Object.keys(regimeEvolution.growth ?? {}).map((yyyymmdd) => {
		return new Date(`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(-2)}T00:00:00Z`).getTime();
	}); // No control if dates are not equal for all series
	const GrowSeries = Object.values(regimeEvolution.growth ?? {});
	const LateralSeries = Object.values(regimeEvolution.lateralPhase ?? {});
	const HighSeries = Object.values(regimeEvolution.highStress ?? {});

	const EvolutionOfRegimeData = {
		DateSeries,
		GrowSeries,
		LateralSeries,
		HighSeries,
	};

	return EvolutionOfRegimeData;
}

export function EvolutionOfRegimeProbabilitiesInner(
	props: {
		title?: string;
		description?: string;
		series: {
			DateSeries: number[];
			GrowSeries: number[];
			LateralSeries: number[];
			HighSeries: number[];
		};
	} & ImperativeHandleRefProps<{ download(): void }>,
): React.JSX.Element {
	const { t } = useTranslation();

	const chartRef = useRef<HighchartsReactRefObject | null>(null);
	useImperativeHandle(
		props.handleRef,
		() => ({
			download() {
				chartRef.current?.chart.exportChartLocal(
					{
						type: "image/jpeg",
					},
					chartRef.current.chart.options,
				);
			},
		}),
		[],
	);

	return (
		<>
			<div style={{ paddingBottom: "1rem" }}>
				{props.description ?? t("EVOLUTION_OF_REGIME_PROBABILITIES.description")}
			</div>
			<PercentageChart
				chartRef={chartRef}
				xTimestamps={props.series.DateSeries}
				title={props.title}
				series={[
					{
						color: "#4cb09c",
						name: t("EVOLUTION_OF_REGIME_PROBABILITIES.growth_tooltip"),
						legendName: t("EVOLUTION_OF_REGIME_PROBABILITIES.growth_legend"),
						yValues: props.series.GrowSeries,
					},
					{
						color: "#ffa800",
						name: t("EVOLUTION_OF_REGIME_PROBABILITIES.lateral_phase_tooltip"),
						legendName: t("EVOLUTION_OF_REGIME_PROBABILITIES.lateral_phase_legend"),
						yValues: props.series.LateralSeries,
					},
					{
						color: "#e94020",
						name: t("EVOLUTION_OF_REGIME_PROBABILITIES.high_stress_tooltip"),
						legendName: t("EVOLUTION_OF_REGIME_PROBABILITIES.high_stress_legend"),
						yValues: props.series.HighSeries,
					},
				]}
			/>
		</>
	);
}

export default withContext(RegionContext)(EvolutionOfRegimeProbabilitiesBlock);
